/**
 * Yup Validation Schemas for Widget Customize Form
 * Comprehensive validation for all widget customization settings
 */

import * as yup from 'yup';

// ============================================
// Color Validation Helpers
// ============================================

/**
 * Validates hex color format (#RGB or #RRGGBB)
 */
const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

/**
 * Validates RGB color format rgb(r, g, b)
 */
const rgbColorRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;

/**
 * Validates HSL color format hsl(h, s%, l%)
 */
const hslColorRegex = /^hsl\(\s*(\d{1,3})\s*,\s*(\d{1,3})%\s*,\s*(\d{1,3})%\s*\)$/;

/**
 * Combined color validation (hex, rgb, or hsl)
 */
const colorValidation = yup
  .string()
  .required('Color is required')
  .test('is-valid-color', 'Invalid color format. Use hex (#RRGGBB), RGB, or HSL', (value) => {
    if (!value) return false;
    return hexColorRegex.test(value) || rgbColorRegex.test(value) || hslColorRegex.test(value);
  });

/**
 * Optional color validation
 */
const optionalColorValidation = yup
  .string()
  .nullable()
  .test('is-valid-color-optional', 'Invalid color format', (value) => {
    if (!value || value === '') return true;
    return hexColorRegex.test(value) || rgbColorRegex.test(value) || hslColorRegex.test(value);
  });

// ============================================
// Phone Number Validation
// ============================================

/**
 * International phone number validation with country code
 * - Only + and digits allowed
 * - Minimum 6 digits, maximum 19 digits (excluding +)
 */
const phoneNumberRegex = /^\+\d{6,19}$/;

/**
 * Regex to allow only + and digits during input
 */
export const phoneInputAllowedChars = /^[+\d]*$/;

const phoneValidation = yup
  .string()
  .required('Phone number is required')
  .test('only-plus-digits', 'Only + and digits are allowed', (value) => {
    if (!value) return false;
    return /^[+\d]+$/.test(value);
  })
  .test('starts-with-plus', 'Phone number must start with +', (value) => {
    if (!value) return false;
    return value.startsWith('+');
  })
  .test('min-digits', 'Phone number must have at least 6 digits', (value) => {
    if (!value) return false;
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length >= 6;
  })
  .test('max-digits', 'Phone number cannot exceed 19 digits', (value) => {
    if (!value) return false;
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length <= 19;
  })
  .matches(phoneNumberRegex, 'Enter valid phone number with country code (e.g., +1234567890)');

// ============================================
// URL Validation
// ============================================

const urlValidation = yup
  .string()
  .nullable()
  .test('is-valid-url', 'Enter a valid URL', (value) => {
    if (!value || value === '') return true;
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  });

// ============================================
// SVG Validation
// ============================================

const svgValidation = yup
  .string()
  .nullable()
  .test('is-valid-svg', 'Invalid SVG format', (value) => {
    if (!value || value === '') return true;
    const trimmed = value.trim();
    return trimmed.startsWith('<svg') && trimmed.endsWith('</svg>');
  });

// ============================================
// Brand Config Validation Schema
// ============================================

export const brandConfigSchema = yup.object().shape({
  brandName: yup
    .string()
    .required('Brand name is required')
    .min(2, 'Brand name must be at least 2 characters')
    .max(50, 'Brand name must not exceed 50 characters')
    .trim(),

  brandSubtitle: yup
    .string()
    .max(100, 'Subtitle must not exceed 100 characters')
    .trim(),

  brandColor: colorValidation,

  brandImageURL: urlValidation,

  phoneNumber: phoneValidation,

  defaultMessage: yup
    .string()
    .max(500, 'Default message must not exceed 500 characters')
    .trim(),

  widgetCTAText: yup
    .string()
    .required('CTA text is required')
    .min(2, 'CTA text must be at least 2 characters')
    .max(30, 'CTA text must not exceed 30 characters')
    .trim(),

  agentName: yup
    .string()
    .required('Agent name is required')
    .min(2, 'Agent name must be at least 2 characters')
    .max(30, 'Agent name must not exceed 30 characters')
    .trim(),

  agentAvatar: urlValidation,

  onlineStatusText: yup
    .string()
    .max(50, 'Status text must not exceed 50 characters')
    .trim(),

  offlineStatusText: yup
    .string()
    .max(50, 'Status text must not exceed 50 characters')
    .trim(),

  welcomeMessage: yup
    .string()
    .required('Welcome message is required')
    .min(5, 'Welcome message must be at least 5 characters')
    .max(300, 'Welcome message must not exceed 300 characters')
    .trim(),
});

// ============================================
// Theme Styles Validation Schema
// ============================================

export const themeStylesSchema = yup.object().shape({
  // Core colors
  primaryColor: colorValidation,
  secondaryColor: colorValidation,
  backgroundColor: colorValidation,
  textColor: colorValidation,

  // Advanced colors
  hoverColor: optionalColorValidation,
  badgeColor: optionalColorValidation,
  statusOnlineColor: optionalColorValidation,
  statusOfflineColor: optionalColorValidation,
  borderColor: optionalColorValidation,
  shadowColor: optionalColorValidation,

  // Hover colors
  hoverBackgroundColor: optionalColorValidation,
  hoverIconColor: optionalColorValidation,

  // Shape & Layout
  buttonShape: yup
    .string()
    .oneOf(['circle', 'rounded', 'pill', 'square'], 'Invalid button shape')
    .required('Button shape is required'),

  borderRadius: yup
    .number()
    .required('Border radius is required')
    .min(0, 'Border radius cannot be negative')
    .max(50, 'Border radius cannot exceed 50px'),

  shadowStyle: yup
    .string()
    .oneOf(['none', 'subtle', 'soft', 'medium', 'strong'], 'Invalid shadow style')
    .required('Shadow style is required'),

  borderStyle: yup
    .string()
    .oneOf(['solid', 'dashed', 'none'], 'Invalid border style')
    .required('Border style is required'),

  borderWidth: yup
    .number()
    .required('Border width is required')
    .min(0, 'Border width cannot be negative')
    .max(10, 'Border width cannot exceed 10px'),

  iconSize: yup
    .number()
    .required('Icon size is required')
    .min(16, 'Icon size must be at least 16px')
    .max(64, 'Icon size cannot exceed 64px'),

  iconPadding: yup
    .number()
    .required('Icon padding is required')
    .min(0, 'Icon padding cannot be negative')
    .max(32, 'Icon padding cannot exceed 32px'),

  // Typography
  fontFamily: yup
    .string()
    .required('Font family is required'),

  fontWeight: yup
    .number()
    .required('Font weight is required')
    .oneOf([300, 400, 500, 600, 700, 800], 'Invalid font weight'),

  fontSize: yup
    .number()
    .required('Font size is required')
    .min(10, 'Font size must be at least 10px')
    .max(24, 'Font size cannot exceed 24px'),

  lineHeight: yup
    .number()
    .required('Line height is required')
    .min(1, 'Line height must be at least 1')
    .max(3, 'Line height cannot exceed 3'),

  textCase: yup
    .string()
    .oneOf(['uppercase', 'lowercase', 'capitalize', 'none'], 'Invalid text case')
    .required('Text case is required'),

  // Button style
  buttonStyle: yup
    .string()
    .oneOf(['solid', 'outline', 'gradient', 'glass'], 'Invalid button style')
    .required('Button style is required'),

  // Gradient support
  useGradient: yup.boolean().required(),

  gradientDirection: yup
    .string()
    .oneOf(['0deg', '45deg', '90deg', '135deg', '180deg', '225deg', '270deg', '315deg'], 'Invalid gradient direction')
    .when('useGradient', {
      is: true,
      then: (schema) => schema.required('Gradient direction is required when gradient is enabled'),
    }),

  gradientType: yup
    .string()
    .oneOf(['linear', 'radial'], 'Invalid gradient type')
    .when('useGradient', {
      is: true,
      then: (schema) => schema.required('Gradient type is required when gradient is enabled'),
    }),

  // Header color
  headerColor: colorValidation,

  // Dark mode
  enableDarkMode: yup.boolean().required(),
});

// ============================================
// Icon Config Validation Schema
// ============================================

export const iconConfigSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf(['whatsapp', 'whatsapp-outline', 'whatsapp-filled', 'custom', 'emoji'], 'Invalid icon type')
    .required('Icon type is required'),

  customSvg: yup
    .string()
    .when('type', {
      is: 'custom',
      then: () => svgValidation.required('Custom SVG is required when icon type is custom'),
      otherwise: () => svgValidation,
    }),

  emoji: yup
    .string()
    .when('type', {
      is: 'emoji',
      then: (schema) => schema
        .required('Emoji is required when icon type is emoji')
        .max(4, 'Emoji must be a single character or emoji'),
      otherwise: (schema) => schema.nullable(),
    }),

  animation: yup
    .string()
    .oneOf(['none', 'pulse', 'wave', 'shake', 'bounce'], 'Invalid icon animation')
    .required('Icon animation is required'),

  color: optionalColorValidation,

  size: yup
    .number()
    .nullable()
    .min(16, 'Icon size must be at least 16px')
    .max(64, 'Icon size cannot exceed 64px'),
});

// ============================================
// Badge Config Validation Schema
// ============================================

export const badgeConfigSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf(['count', 'dot', 'none'], 'Invalid badge type')
    .required('Badge type is required'),

  text: yup
    .string()
    .when('type', {
      is: 'count',
      then: (schema) => schema
        .required('Badge text is required for count type')
        .max(3, 'Badge text must not exceed 3 characters'),
      otherwise: (schema) => schema.nullable(),
    }),

  backgroundColor: colorValidation,

  textColor: colorValidation,

  position: yup
    .string()
    .oneOf(['top-right', 'top-left', 'bottom-right', 'bottom-left'], 'Invalid badge position')
    .required('Badge position is required'),

  animated: yup.boolean().required(),
});

// ============================================
// Responsive Config Validation Schema
// ============================================

const deviceConfigSchema = yup.object().shape({
  enabled: yup.boolean().required(),
  position: yup
    .string()
    .oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left', 'center-modal', 'inline-container', 'sticky-footer'])
    .nullable(),
  offsetX: yup
    .number()
    .required('Offset X is required')
    .min(-500, 'Offset X cannot be less than -500')
    .max(500, 'Offset X cannot exceed 500'),
  offsetY: yup
    .number()
    .required('Offset Y is required')
    .min(-500, 'Offset Y cannot be less than -500')
    .max(500, 'Offset Y cannot exceed 500'),
  scale: yup
    .number()
    .required('Scale is required')
    .min(0.5, 'Scale must be at least 0.5')
    .max(2, 'Scale cannot exceed 2'),
});

export const responsiveConfigSchema = yup.object().shape({
  desktop: deviceConfigSchema,
  mobile: deviceConfigSchema.shape({
    useStickyFooter: yup.boolean().required(),
  }),
  tablet: deviceConfigSchema,
  hideOnScreenSizes: yup
    .array()
    .of(yup.string().oneOf(['mobile', 'tablet', 'desktop'])),
  safeAreaHandling: yup.boolean().required(),
});

// ============================================
// Placement Config Validation Schema
// ============================================

export const placementConfigSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left', 'center-modal', 'inline-container', 'sticky-footer'], 'Invalid placement type')
    .required('Placement type is required'),

  marginTop: yup
    .number()
    .required('Margin top is required')
    .min(0, 'Margin cannot be negative')
    .max(200, 'Margin cannot exceed 200px'),

  marginBottom: yup
    .number()
    .required('Margin bottom is required')
    .min(0, 'Margin cannot be negative')
    .max(200, 'Margin cannot exceed 200px'),

  marginLeft: yup
    .number()
    .required('Margin left is required')
    .min(0, 'Margin cannot be negative')
    .max(200, 'Margin cannot exceed 200px'),

  marginRight: yup
    .number()
    .required('Margin right is required')
    .min(0, 'Margin cannot be negative')
    .max(200, 'Margin cannot exceed 200px'),

  offsetX: yup
    .number()
    .required('Offset X is required')
    .min(-500, 'Offset X cannot be less than -500')
    .max(500, 'Offset X cannot exceed 500'),

  offsetY: yup
    .number()
    .required('Offset Y is required')
    .min(-500, 'Offset Y cannot be less than -500')
    .max(500, 'Offset Y cannot exceed 500'),

  animationType: yup
    .string()
    .oneOf(['fade', 'slide', 'scale', 'bounce', 'zoom', 'none'], 'Invalid animation type')
    .required('Animation type is required'),

  animationDuration: yup
    .number()
    .required('Animation duration is required')
    .min(0, 'Duration cannot be negative')
    .max(2000, 'Duration cannot exceed 2000ms'),

  zIndex: yup
    .number()
    .required('Z-index is required')
    .min(1, 'Z-index must be at least 1')
    .max(9999999, 'Z-index is too high'),

  containerSelector: yup
    .string()
    .nullable()
    .when('type', {
      is: 'inline-container',
      then: (schema) => schema.required('Container selector is required for inline placement'),
    }),

  responsive: responsiveConfigSchema,
});

// ============================================
// Animation Config Validation Schema
// ============================================

export const animationConfigSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf(['fade', 'slide', 'scale', 'bounce', 'zoom', 'none'], 'Invalid animation type')
    .required('Animation type is required'),

  duration: yup
    .number()
    .required('Duration is required')
    .min(0, 'Duration cannot be negative')
    .max(2000, 'Duration cannot exceed 2000ms'),

  delay: yup
    .number()
    .required('Delay is required')
    .min(0, 'Delay cannot be negative')
    .max(5000, 'Delay cannot exceed 5000ms'),

  easing: yup
    .string()
    .oneOf(['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear'], 'Invalid easing type')
    .required('Easing is required'),

  enablePulse: yup.boolean().required(),

  pulseColor: yup
    .string()
    .when('enablePulse', {
      is: true,
      then: () => optionalColorValidation,
    }),

  pulseScale: yup
    .number()
    .when('enablePulse', {
      is: true,
      then: (schema) => schema.min(1, 'Pulse scale must be at least 1').max(2, 'Pulse scale cannot exceed 2'),
    }),

  enableNudge: yup.boolean().required(),

  nudgeDelay: yup
    .number()
    .when('enableNudge', {
      is: true,
      then: (schema) => schema
        .required('Nudge delay is required')
        .min(1, 'Nudge delay must be at least 1 second')
        .max(300, 'Nudge delay cannot exceed 300 seconds'),
    }),

  nudgeType: yup
    .string()
    .oneOf(['shake', 'bounce', 'pulse', 'none'], 'Invalid nudge type')
    .required('Nudge type is required'),

  respectReducedMotion: yup.boolean().required(),
});

// ============================================
// Floating Badge Validation Schema
// ============================================

export const floatingBadgeConfigSchema = yup.object().shape({
  enabled: yup.boolean().required(),

  message: yup
    .string()
    .max(100, 'Message must not exceed 100 characters')
    .when('enabled', {
      is: true,
      then: (schema) => schema.required('Message is required when floating badge is enabled'),
    }),

  backgroundColor: optionalColorValidation,

  textColor: optionalColorValidation,

  fontSize: yup
    .number()
    .min(10, 'Font size must be at least 10px')
    .max(24, 'Font size cannot exceed 24px'),

  glossyEffect: yup.boolean(),

  borderColor: optionalColorValidation,

  borderWidth: yup
    .number()
    .min(0, 'Border width cannot be negative')
    .max(5, 'Border width cannot exceed 5px'),

  position: yup
    .string()
    .oneOf(['left', 'right', 'top', 'bottom'], 'Invalid position'),

  maxWidth: yup
    .number()
    .min(100, 'Max width must be at least 100px')
    .max(400, 'Max width cannot exceed 400px'),

  textAlign: yup
    .string()
    .oneOf(['left', 'center', 'right'], 'Invalid text alignment'),
});

// ============================================
// Behavior Config Validation Schema
// ============================================

export const behaviorConfigSchema = yup.object().shape({
  openOnMobile: yup.boolean().required(),

  openByDefault: yup.boolean().required(),

  reopenSessionWindow: yup
    .string()
    .oneOf(['ALWAYS', 'ONCE', '24_HOUR', '7_DAY'], 'Invalid session window')
    .required('Session window is required'),

  showBadge: yup.boolean().required(),

  badgeText: yup
    .string()
    .when('showBadge', {
      is: true,
      then: (schema) => schema.required('Badge text is required').max(3, 'Badge text must not exceed 3 characters'),
    }),

  soundEnabled: yup.boolean().required(),

  floatingBadge: floatingBadgeConfigSchema,
});

// ============================================
// Widget Name Validation
// ============================================

export const widgetNameSchema = yup
  .string()
  .required('Widget name is required')
  .min(3, 'Widget name must be at least 3 characters')
  .max(50, 'Widget name must not exceed 50 characters')
  .matches(/^[a-zA-Z0-9\s\-_]+$/, 'Widget name can only contain letters, numbers, spaces, hyphens, and underscores')
  .trim();

// ============================================
// Complete Customize Form Schema
// ============================================

export const customizeFormSchema = yup.object().shape({
  widgetName: widgetNameSchema,
  brandConfig: brandConfigSchema,
  themeStyles: themeStylesSchema,
  iconConfig: iconConfigSchema,
  badgeConfig: badgeConfigSchema,
  placementConfig: placementConfigSchema,
  animationConfig: animationConfigSchema,
  behaviorConfig: behaviorConfigSchema,
});

// ============================================
// Utility Functions
// ============================================

/**
 * Validate a single field
 */
export const validateField = async <T>(
  schema: yup.Schema<T>,
  value: unknown
): Promise<{ isValid: boolean; error?: string }> => {
  try {
    await schema.validate(value);
    return { isValid: true };
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      return { isValid: false, error: err.message };
    }
    return { isValid: false, error: 'Validation error' };
  }
};

/**
 * Validate entire form
 */
export const validateForm = async (
  formData: unknown
): Promise<{ isValid: boolean; errors: Record<string, string> }> => {
  try {
    await customizeFormSchema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (err) {
    if (err instanceof yup.ValidationError) {
      const errors: Record<string, string> = {};
      err.inner.forEach((e) => {
        if (e.path) {
          errors[e.path] = e.message;
        }
      });
      return { isValid: false, errors };
    }
    return { isValid: false, errors: { _form: 'Validation error' } };
  }
};

/**
 * Check WCAG contrast ratio
 * Returns the contrast ratio between two colors
 */
export const calculateContrastRatio = (color1: string, color2: string): number => {
  const getLuminance = (hex: string): number => {
    const rgb = hex.replace('#', '').match(/.{2}/g);
    if (!rgb) return 0;

    const [r, g, b] = rgb.map((c) => {
      const val = parseInt(c, 16) / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if contrast ratio meets WCAG AA standard (4.5:1 for normal text)
 */
export const isWCAGCompliant = (foreground: string, background: string, level: 'AA' | 'AAA' = 'AA'): boolean => {
  const ratio = calculateContrastRatio(foreground, background);
  return level === 'AA' ? ratio >= 4.5 : ratio >= 7;
};

/**
 * Hex color validation helper
 */
export const isValidHexColor = (color: string): boolean => hexColorRegex.test(color);

/**
 * Phone number validation helper
 * Validates: starts with +, only digits after +, 6-19 digits total
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone) return false;
  // Check only + and digits allowed
  if (!/^[+\d]+$/.test(phone)) return false;
  // Must start with +
  if (!phone.startsWith('+')) return false;
  // Check digit count (6-19 digits)
  const digitsOnly = phone.replace(/\D/g, '');
  if (digitsOnly.length < 6 || digitsOnly.length > 19) return false;
  return phoneNumberRegex.test(phone);
};

/**
 * URL validation helper
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Export all schemas
export default {
  brandConfigSchema,
  themeStylesSchema,
  iconConfigSchema,
  badgeConfigSchema,
  placementConfigSchema,
  animationConfigSchema,
  behaviorConfigSchema,
  floatingBadgeConfigSchema,
  widgetNameSchema,
  customizeFormSchema,
  responsiveConfigSchema,
};
